/**
 * Wandelt Quill-HTML in e-mail-taugliches HTML um
 * (Inline-Styles statt ql-* Klassen).
 *
 * Zeilenabstand wie bei der Signatur: kein Absatz-Margin,
 * Umbrüche als <br>, line-height: 1.5.
 */

const SIGNATURE_LINE_HEIGHT = '1.5';

function isBlankParagraph(el) {
  return (
    !String(el.textContent || '').trim() ||
    /^(\s|<br\s*\/?>|&nbsp;|\u00a0)*$/i.test(el.innerHTML || '')
  );
}

function isSimpleParagraph(el) {
  return !el.querySelector('div, p, ul, ol, table, blockquote, h1, h2, h3, h4, h5, h6, pre');
}

/**
 * Aufeinanderfolgende einfache <p> (wie Quill Enter) → Signatur-Stil mit <br>.
 * So entstehen dieselben Abstände wie bei signatureText / textToHtmlLines.
 */
function flattenParagraphsLikeSignature(root) {
  const nodes = Array.from(root.childNodes);
  const out = document.createDocumentFragment();
  let i = 0;

  while (i < nodes.length) {
    const node = nodes[i];
    if (
      node.nodeType === 1 &&
      node.tagName === 'P' &&
      isSimpleParagraph(node)
    ) {
      const run = [];
      while (
        i < nodes.length &&
        nodes[i].nodeType === 1 &&
        nodes[i].tagName === 'P' &&
        isSimpleParagraph(nodes[i])
      ) {
        run.push(nodes[i]);
        i += 1;
      }

      const hasAlign = run.some(
        (p) => p.style.textAlign && p.style.textAlign !== 'left'
      );

      if (hasAlign) {
        run.forEach((p) => {
          p.style.margin = '0';
          p.style.padding = '0';
          p.style.lineHeight = SIGNATURE_LINE_HEIGHT;
          out.appendChild(p);
        });
      } else {
        const wrap = document.createElement('div');
        wrap.style.margin = '0';
        wrap.style.lineHeight = SIGNATURE_LINE_HEIGHT;
        run.forEach((p, idx) => {
          if (idx > 0) wrap.appendChild(document.createElement('br'));
          if (!isBlankParagraph(p)) {
            while (p.firstChild) wrap.appendChild(p.firstChild);
          }
        });
        out.appendChild(wrap);
      }
      continue;
    }

    out.appendChild(node);
    i += 1;
  }

  root.innerHTML = '';
  root.appendChild(out);
}

export function quillHtmlToEmailHtml(html = '') {
  if (!html || typeof document === 'undefined') return String(html || '');

  const root = document.createElement('div');
  root.innerHTML = String(html);

  root.querySelectorAll('*').forEach((el) => {
    const classList = Array.from(el.classList || []);
    classList.forEach((cls) => {
      if (cls === 'ql-align-center') el.style.textAlign = 'center';
      else if (cls === 'ql-align-right') el.style.textAlign = 'right';
      else if (cls === 'ql-align-left') el.style.textAlign = 'left';
      else if (cls === 'ql-align-justify') el.style.textAlign = 'justify';
      else if (cls === 'ql-direction-rtl') el.style.direction = 'rtl';
      else if (/^ql-indent-(\d+)$/.test(cls)) {
        const level = Number(RegExp.$1) || 1;
        el.style.paddingLeft = `${level * 24}px`;
      } else if (/^ql-size-/.test(cls)) {
        if (cls === 'ql-size-small') el.style.fontSize = '12px';
        if (cls === 'ql-size-large') el.style.fontSize = '18px';
        if (cls === 'ql-size-huge') el.style.fontSize = '24px';
      }
      if (cls.startsWith('ql-')) el.classList.remove(cls);
    });

    if (!el.getAttribute('class')) el.removeAttribute('class');
  });

  root.querySelectorAll('a').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
    if (!a.style.color) a.style.color = '#2563eb';
    if (!a.style.textDecoration) a.style.textDecoration = 'underline';
  });

  // Vor dem Flatten: verbleibende Block-Elemente ohne Extra-Margin
  root.querySelectorAll('p, h1, h2, h3, li, blockquote, pre, code').forEach((el) => {
    if (el.tagName === 'P') {
      el.style.margin = '0';
      el.style.padding = '0';
      el.style.lineHeight = SIGNATURE_LINE_HEIGHT;
    }
    if (el.tagName === 'H1') {
      el.style.fontSize = el.style.fontSize || '22px';
      el.style.margin = el.style.margin || '0 0 0.45em';
      el.style.fontWeight = '700';
    }
    if (el.tagName === 'H2') {
      el.style.fontSize = el.style.fontSize || '18px';
      el.style.margin = el.style.margin || '0 0 0.4em';
      el.style.fontWeight = '700';
    }
    if (el.tagName === 'H3') {
      el.style.fontSize = el.style.fontSize || '16px';
      el.style.margin = el.style.margin || '0 0 0.35em';
      el.style.fontWeight = '700';
    }
    if (el.tagName === 'BLOCKQUOTE') {
      el.style.borderLeft = el.style.borderLeft || '3px solid #d4d4d4';
      el.style.margin = el.style.margin || '0 0 0.45em';
      el.style.padding = el.style.padding || '0 0 0 12px';
      el.style.color = el.style.color || '#525252';
    }
    if (el.tagName === 'PRE') {
      el.style.background = el.style.background || '#f4f4f5';
      el.style.border = el.style.border || '1px solid #e4e4e7';
      el.style.borderRadius = el.style.borderRadius || '8px';
      el.style.padding = el.style.padding || '10px 12px';
      el.style.margin = el.style.margin || '0 0 0.45em';
      el.style.fontFamily = el.style.fontFamily || 'Consolas, Monaco, monospace';
      el.style.fontSize = el.style.fontSize || '13px';
      el.style.whiteSpace = el.style.whiteSpace || 'pre-wrap';
      el.style.overflowWrap = 'break-word';
    }
    if (el.tagName === 'CODE' && el.parentElement?.tagName !== 'PRE') {
      el.style.background = el.style.background || '#f4f4f5';
      el.style.borderRadius = el.style.borderRadius || '4px';
      el.style.padding = el.style.padding || '1px 5px';
      el.style.fontFamily = el.style.fontFamily || 'Consolas, Monaco, monospace';
      el.style.fontSize = el.style.fontSize || '0.92em';
    }
  });

  root.querySelectorAll('ul, ol').forEach((el) => {
    el.style.margin = el.style.margin || '0 0 0.45em';
    el.style.paddingLeft = el.style.paddingLeft || '1.4em';
  });

  flattenParagraphsLikeSignature(root);

  return root.innerHTML;
}

/** Vollständiges HTML-Mail-Dokument für bessere Client-Kompatibilität */
export function wrapEmailHtmlDocument(bodyHtml = '') {
  return (
    `<!DOCTYPE html>` +
    `<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">` +
    `<title>E-Mail</title></head>` +
    `<body style="margin:0;padding:16px;background:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.5;color:#1f2937;">` +
    `<div style="max-width:680px;margin:0 auto;">${bodyHtml}</div>` +
    `</body></html>`
  );
}

export function prepareComposerHtmlForSend(quillHtml = '') {
  return wrapEmailHtmlDocument(quillHtmlToEmailHtml(quillHtml));
}
